import React, { useState } from 'react';
import { Alert, Button, Input, InputNumber, Space, Typography } from 'antd';
import adminAxios from './adminAxios';
import { resolveFrontendApiBase } from '../utils/apiBase';

const API_BASE = resolveFrontendApiBase();
type Preview = { confirmationId: string; digest: string; recipientCount: number; templateId: number; expiresAt: string };

export default function AdminCommunications() {
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [recipients, setRecipients] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');
  const [recoveryRequired, setRecoveryRequired] = useState(false);
  const reset = () => { setPreview(null); setError(''); setResult(''); setRecoveryRequired(false); };
  const showError = (failure: any) => {
    const status = failure?.response?.status;
    const detail = failure?.response?.data?.detail;
    setError(status === 401 || status === 403 ? 'Administrator sign-in is required. Sign in again before confirming.' :
      typeof detail === 'string' ? detail : 'The request did not finish. Retry the same confirmation to check its status.');
    if (status === 503 || (status === 409 && typeof detail === 'string' && detail.includes('reconciliation'))) setRecoveryRequired(true);
  };
  const createPreview = async () => {
    reset(); setBusy(true);
    try {
      const addresses = recipients.split(/[\n,;]/).map(value => value.trim()).filter(Boolean);
      const { data } = await adminAxios.post<Preview>(`${API_BASE}/brevo/communications/preview`, {
        template_id: templateId, recipients: addresses.map(email => ({ email })),
      });
      setPreview(data);
    } catch (failure) { showError(failure); } finally { setBusy(false); }
  };
  const confirm = async () => {
    if (!preview) return;
    setBusy(true); setError('');
    try {
      const { data } = await adminAxios.post<{ sent: number }>(`${API_BASE}/brevo/communications/confirm`, {
        confirmationId: preview.confirmationId, digest: preview.digest,
      });
      setResult(`Sent to ${data.sent} recipient${data.sent === 1 ? '' : 's'}.`);
      setPreview(null);
    } catch (failure) { showError(failure); } finally { setBusy(false); }
  };
  return <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 700 }}>
    <Typography.Paragraph>Select a Brevo template and up to 50 exact email recipients. Review the count before sending. The template content is frozen for this confirmation.</Typography.Paragraph>
    <label htmlFor="communication-template">Template ID</label>
    <InputNumber id="communication-template" min={1} value={templateId} disabled={busy || Boolean(preview)} onChange={value => { reset(); setTemplateId(value); }} />
    <label htmlFor="communication-recipients">Recipients, one per line</label>
    <Input.TextArea id="communication-recipients" rows={6} maxLength={16000} value={recipients} disabled={busy || Boolean(preview)} onChange={event => { reset(); setRecipients(event.target.value); }} />
    {error && <Alert type="error" showIcon message={error} />}
    {result && <Alert type="success" showIcon message={result} />}
    {preview && <Alert type="info" message={`Confirm template ${preview.templateId} for ${preview.recipientCount} recipients. Expires ${new Date(preview.expiresAt).toLocaleTimeString()}.`} />}
    {recoveryRequired && <Typography.Paragraph>Operation {preview?.confirmationId} requires provider reconciliation. Keep this reference and resolve its delivery status before creating another send.</Typography.Paragraph>}
    {!preview ? <Button onClick={createPreview} loading={busy} disabled={!templateId || !recipients.trim()}>Preview send</Button> :
      <Space><Button type="primary" onClick={confirm} loading={busy} disabled={recoveryRequired}>Confirm send</Button>
        <Button onClick={reset} disabled={busy || recoveryRequired}>Edit selection</Button></Space>}
  </Space>;
}
